import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Provider } from '@kaltura-ott/tvpil';

import Img from 'components/presenters/Img/Img';
import ActionButton from 'components/widgets/ActionButton/ActionButton';
import Checkbox from 'components/widgets/common/Checkbox/Checkbox';
import { DefaultModal } from 'components/widgets/Modal/DefaultModal/DefaultModal';
import { DO_NOT_SHOW_AGAIN } from 'consts';
import { ActionButtonTypesEnum } from 'interface';
import localStorageService from 'services/localStorageService';

import styles from './ProviderActivationModal.module.scss';

interface Props {
  provider: string;
  providerLogo: string;
  providerSettings: Provider;
  watchAsset(): void;
}

const COMPONENT_NAME = 'ProviderActivationModal';

function ProviderActivationModal({ provider, providerLogo, providerSettings, watchAsset }: Props) {
  const { fullActivationUrl, isConfigured, shortActivationUrl, code } = providerSettings;
  const intl = useIntl();
  const title = intl.formatMessage({ defaultMessage: `Do you have ${provider} account?`, id: `${COMPONENT_NAME}.titleForRegistered.${provider}` });
  const buttonLabel = intl.formatMessage({ defaultMessage: `Go to ${provider}`, id: `${COMPONENT_NAME}.actionBtn.${provider}` });
  const description = intl.formatMessage({ defaultMessage: `Open ${provider} to start watching`, id: `${COMPONENT_NAME}.descriptionForRegistered.${provider}` });
  const borderText = intl.formatMessage({ defaultMessage: `Don't have ${provider} account?`, id: `${COMPONENT_NAME}.titleForUnregistered.${provider}` });
  const descriptionForUnregistered = intl.formatMessage({ defaultMessage: 'Click here:', id: `${COMPONENT_NAME}.descriptionForUnregistered.${provider}` });
  const activationInstructionText = intl.formatMessage({ defaultMessage: 'And enter this code:', id: `${COMPONENT_NAME}.activationInstruction.${provider}` });
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      localStorageService.setItem(`${provider}${DO_NOT_SHOW_AGAIN}`, checked);
    },
    [provider],
  );

  return (
    <DefaultModal className={styles.modal} icon={<Img alt={`${provider} logo`} src={providerLogo} />}>
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
        <ActionButton title={buttonLabel} type={ActionButtonTypesEnum.primary} onClick={watchAsset} />
        <Checkbox
          translationId={`${COMPONENT_NAME}.doNotShowAgain.${provider}`}
          defaultMessage='Don’t show this again'
          onChange={handleCheckboxChange}
        />
      </div>
      <div className={styles.withBorder}>
        <h3>{borderText}</h3>
        {isConfigured ? (
          <>
            <p>{descriptionForUnregistered}</p>
            <a aria-label={shortActivationUrl} href={fullActivationUrl} rel='noopener noreferrer'>
              <p>{shortActivationUrl}</p>
            </a>
            {code && (
              <>
                <p>{activationInstructionText}</p>
                <span className={styles.code}>{code}</span>
              </>
            )}
          </>
        ) : (
          <FormattedMessage
            defaultMessage='It seems that your activation code was not provided yet. You need it for the fist registration.{br}Please write email to <strong>ott@o2.cz</strong> or try later.'
            id={`${COMPONENT_NAME}.notConfigured.${provider}`}
            tagName='p'
            values={{
              br: <br />,
              strong: (text: React.ReactNode) => <strong>{text}</strong>,
            }}
          />
        )}
      </div>
    </DefaultModal>
  );
}

export default ProviderActivationModal;
